"use client";

import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import { memo, useCallback, useState } from "react";
import { m } from "framer-motion";
import { LockKeyhole } from "lucide-react";
import { toast } from "sonner";

import Magnetic from "@/components/motion/Magnetic";
import WishlistButton from "@/app/products/_components/WishlistButton";
import { useCartActions, getCartItemMaxQuantity, useCartRef } from "@/contexts/CartContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { FALLBACK_IMAGE, cn, formatPrice } from "@/lib/shared/utils";
import { getStock } from "@/lib/commerce/stock";
import type { ProductCardProps } from "@/types/product";
import { useAnalytics } from "@/lib/analytics/client";
import { AnalyticsEvents, trackClientEvent } from "@/lib/analytics/events";

const MobileProductSheet = dynamic(() => import("@/app/products/_components/MobileProductSheet"), {
  ssr: false,
});

import type { ProductVisibility } from "@/types/drop";

type ProductCardOptions = {
  index?: number;
  className?: string;
  compact?: boolean;
  archiveMode?: boolean;
  visibility?: ProductVisibility;
};

function ProductCard({
  name,
  price,
  images,
  id,
  sizes,
  stock,
  is_preorder = false,
  low_stock_threshold = 3,
  purchase_limit = null,
  preorder_limit = null,
  index,
  className,
  compact = false,
  archiveMode = false,
  visibility,
}: ProductCardProps & ProductCardOptions) {
  const { addItem } = useCartActions();
  const cartRef = useCartRef();
  const isMobile = useIsMobile();
  const analytics = useAnalytics();
  const [isAdding, setIsAdding] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const fireSelectItem = useCallback(() => {
    trackClientEvent(analytics, AnalyticsEvents.ProductClicked, {
      item_id: String(id),
      item_name: name,
      item_category: undefined,
      price,
      index: typeof index === "number" ? index : undefined,
    });
  }, [analytics, id, name, price, index]);

  const totalStock = getStock(stock);
  const isOutOfStock = !is_preorder && totalStock <= 0;
  const isLowStock = !is_preorder && !isOutOfStock && totalStock <= low_stock_threshold;
  const isLocked = visibility === "teaser" || visibility === "none";
  const isViewOnly = visibility === "preview" || isLocked;
  const showViewOnlyBadge = visibility === "preview";
  const canPurchase = !isViewOnly;
  const shouldUseBlur = isLocked;
  const primaryImage = shouldUseBlur ? `/api/blur-image?id=${id}` : images[0] || FALLBACK_IMAGE;
  const secondaryImage = shouldUseBlur ? null : images.length > 1 ? images[1] : null;

  const handleAddToCart = useCallback(
    (selectedSize?: string) => {
      if (!canPurchase || isAdding || isOutOfStock) {
        if (isOutOfStock) toast.error(`${name} is out of stock`);
        return;
      }

      const size = selectedSize || sizes[0];
      if (!size || (sizes.length > 1 && !selectedSize)) {
        toast.error("Please select a size");
        return;
      }
      if (!is_preorder && getStock(stock, size) <= 0) {
        toast.error(`Size ${size} is out of stock`);
        return;
      }
      if (Number.isNaN(price)) return;

      const maxQty = getCartItemMaxQuantity({
        id,
        name,
        price,
        image: primaryImage,
        size,
        quantity: 1,
        stock,
        is_preorder,
        purchase_limit: purchase_limit ?? null,
        preorder_limit: preorder_limit ?? null,
      });
      const currentQty = cartRef.current.find((i) => i.id === id && i.size === size)?.quantity ?? 0;

      if (maxQty <= 0) {
        toast.error(`Size ${size} is out of stock`);
        return;
      }
      if (currentQty >= maxQty) {
        toast.error("Max available quantity reached");
        return;
      }

      setIsAdding(true);
      addItem({
        id,
        name,
        price,
        image: primaryImage,
        size,
        quantity: 1,
        stock,
        is_preorder,
        purchase_limit: purchase_limit ?? null,
        preorder_limit: preorder_limit ?? null,
      });
      toast.success(`${name} (${size}) added to cart`);
      window.setTimeout(() => setIsAdding(false), 300);
    },
    [
      addItem,
      canPurchase,
      id,
      isAdding,
      isOutOfStock,
      is_preorder,
      name,
      price,
      primaryImage,
      sizes,
      stock,
      purchase_limit,
      preorder_limit,
      cartRef,
    ]
  );

  return (
    <m.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: isMobile ? 0 : 0.4, delay: isMobile ? 0 : (index || 0) * 0.02 }}
      className={cn("group/card flex h-full flex-col", className)}
    >
      <div
        className={cn(
          "bg-muted group-hover/card:border-foreground/10 relative mb-4 w-full overflow-hidden border border-transparent shadow-sm transition-all duration-500 group-hover/card:-translate-y-1 group-hover/card:shadow-xl",
          compact ? "aspect-square" : "aspect-[2/3]"
        )}
      >
        {shouldUseBlur ? (
          <div className="absolute inset-0">
            <Image
              src={primaryImage}
              alt="Locked product teaser"
              fill
              unoptimized
              sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 25vw"
              className="object-contain object-center"
            />
            <div className="bg-background/70 text-foreground absolute inset-0 flex items-center justify-center gap-2 text-[10px] font-bold tracking-[0.18em] uppercase backdrop-blur-[2px]">
              <LockKeyhole data-icon="inline-start" />
              Locked
            </div>
          </div>
        ) : (
          <Link
            href={`/products/${id}`}
            className="absolute inset-0 z-0"
            aria-label={`View ${name}`}
            onClick={fireSelectItem}
          >
            <Image
              src={primaryImage}
              alt={name}
              fill
              priority={typeof index === "number" && index < 4}
              fetchPriority={typeof index === "number" && index < 4 ? "high" : "auto"}
              unoptimized={shouldUseBlur}
              sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 25vw"
              className={cn(
                "object-contain object-center transition-all duration-700 ease-out",
                secondaryImage ? "group-hover/card:opacity-0" : "group-hover/card:scale-110"
              )}
            />
            {secondaryImage && (
              <Image
                src={secondaryImage}
                alt=""
                fill
                loading={typeof index === "number" && index < 4 ? "eager" : "lazy"}
                sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 25vw"
                className="absolute inset-0 object-contain object-center opacity-0 transition-all duration-700 ease-out group-hover/card:scale-110 group-hover/card:opacity-100 group-focus-within/card:opacity-100"
              />
            )}
          </Link>
        )}

        <div className="absolute top-3 right-3 z-30" role="button" tabIndex={0} onKeyDown={(e)=>{if(e.key==="Enter"||e.key===" "){e.preventDefault(); (e.currentTarget as HTMLElement).click()}}} onClick={(event) => event.stopPropagation()}>
          <WishlistButton productId={id} preventSave={isLocked} />
        </div>

        {(showViewOnlyBadge ||
          archiveMode ||
          is_preorder ||
          (canPurchase && (isOutOfStock || isLowStock))) && (
          <span className="border-foreground bg-background text-foreground absolute top-3 left-3 z-10 border px-2 py-1 text-[9px] font-bold tracking-widest uppercase">
            {showViewOnlyBadge
              ? "View only"
              : archiveMode
                ? "Archive"
                : is_preorder
                  ? "Pre-order"
                  : isOutOfStock
                    ? "Sold out"
                    : "Low stock"}
          </span>
        )}

        {isMobile && canPurchase && (
          <MobileProductSheet
            name={name}
            sizes={sizes}
            isSheetOpen={isSheetOpen}
            setIsSheetOpen={setIsSheetOpen}
            handleAddToCart={handleAddToCart}
            isSizeOutOfStock={(size) => !is_preorder && getStock(stock, size) <= 0}
          />
        )}

        {!isMobile && (
          <div className="border-foreground/10 bg-background absolute inset-x-0 bottom-0 z-20 translate-y-full border-t p-4 transition-transform duration-300 ease-out group-hover/card:translate-y-0 group-focus-within/card:translate-y-0">
            {archiveMode ? (
              <Magnetic>
                <Link
                  href={`/products/${id}`}
                  onClick={fireSelectItem}
                  className="bg-foreground text-background hover:bg-foreground/90 flex h-12 w-full items-center justify-center text-xs font-black tracking-[0.2em] uppercase transition-colors"
                >
                  View Details
                </Link>
              </Magnetic>
            ) : !canPurchase ? (
              <Magnetic>
                {isLocked ? (
                  <div className="bg-muted text-muted-foreground flex h-12 w-full items-center justify-center gap-2 text-xs font-black tracking-[0.2em] uppercase">
                    <LockKeyhole data-icon="inline-start" />
                    Locked
                  </div>
                ) : (
                  <Link
                    href={`/products/${id}`}
                    onClick={fireSelectItem}
                    className="bg-foreground text-background hover:bg-foreground/90 flex h-12 w-full items-center justify-center text-xs font-black tracking-[0.2em] uppercase transition-colors"
                  >
                    View Details
                  </Link>
                )}
              </Magnetic>
            ) : sizes.length === 1 ? (
              <Magnetic>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleAddToCart(sizes[0]);
                  }}
                  disabled={isAdding || isOutOfStock}
                  className="bg-foreground text-background hover:bg-foreground/90 disabled:bg-muted disabled:text-muted-foreground h-12 w-full text-xs font-black tracking-[0.2em] uppercase transition-colors disabled:cursor-not-allowed"
                >
                  {isAdding
                    ? "Adding…"
                    : isOutOfStock
                      ? "Out of Stock"
                      : is_preorder
                        ? "Pre-order"
                        : "Add to Cart"}
                </button>
              </Magnetic>
            ) : (
              <div className="flex flex-col gap-3">
                <p className="text-foreground/60 text-center text-[9px] font-black tracking-[0.3em] uppercase">
                  Select size
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {sizes.map((size) => {
                    const sizeOut = !is_preorder && getStock(stock, size) <= 0;
                    return (
                      <button
                        key={size}
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleAddToCart(size);
                        }}
                        disabled={isAdding || sizeOut}
                        className={cn(
                          "group/size border-input text-foreground hover:border-foreground relative flex h-11 cursor-pointer items-center justify-center overflow-hidden border text-xs font-black uppercase transition-colors disabled:cursor-not-allowed disabled:opacity-30",
                          sizeOut && "bg-muted text-muted-foreground line-through"
                        )}
                      >
                        {!sizeOut && (
                          <span className="bg-foreground absolute inset-0 origin-left scale-x-0 transition-transform duration-300 group-hover/size:scale-x-100" />
                        )}
                        <span className="group-hover/size:text-background relative z-10 transition-colors">
                          {size}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {!shouldUseBlur && (
        <div className={cn("flex flex-col gap-1 px-1", compact && "gap-1")}>
          <Link
            href={`/products/${id}`}
            onClick={fireSelectItem}
            className="text-foreground hover:text-muted-foreground truncate font-bold tracking-tight uppercase transition-colors"
          >
            <h3 className={compact ? "text-[11px]" : "text-sm"}>{name}</h3>
          </Link>
          <p
            className={cn(
              "text-muted-foreground font-medium tracking-wide",
              compact ? "text-xs" : "text-sm"
            )}
          >
            {formatPrice(price)}
          </p>
        </div>
      )}
    </m.article>
  );
}

export default memo(ProductCard);
